// Conteúdo do Guia de Configuração (Fase 9 — central de documentação interna).
// Estrutura: cada tópico tem id, titulo, icone e blocos. Cada bloco pode ser
// { tipo: 'paragrafo', texto }, { tipo: 'passos', itens: [] },
// { tipo: 'codigo', linguagem, texto } ou { tipo: 'nota', texto }.

const GUIA = [
  {
    id: 'visao-geral',
    titulo: 'Visão geral',
    icone: 'fa-circle-info',
    blocos: [
      { tipo: 'paragrafo', texto: 'Este guia explica, em detalhe, as áreas de configuração do Tacoma e as integrações disponíveis. Cada tópico abaixo descreve o que a função faz, onde configurá-la e, quando há integração externa, como consumi-la.' },
      { tipo: 'paragrafo', texto: 'A maioria das telas citadas fica no menu Config. As integrações de saída (Webhooks) e a API pública permitem conectar o Tacoma a outros sistemas.' },
    ],
  },
  {
    id: 'webhooks',
    titulo: 'Webhooks (notificações de saída)',
    icone: 'fa-bolt',
    blocos: [
      { tipo: 'paragrafo', texto: 'Um webhook faz o Tacoma avisar um sistema externo no instante em que um evento ocorre, enviando uma requisição HTTP POST para uma URL que você cadastra. É o contrário de ficar consultando o sistema de tempos em tempos.' },
      { tipo: 'paragrafo', texto: 'Onde configurar: Config → Automação → Webhooks. Clique em "Novo webhook", informe um nome, a URL de destino e marque os eventos desejados.' },
      { tipo: 'paragrafo', texto: 'Eventos disponíveis: chamado criado, status alterado, chamado atribuído, SLA em risco e SLA violado. Marque "Todos" para receber qualquer um deles.' },
      { tipo: 'paragrafo', texto: 'Quando o evento acontece, o Tacoma envia um corpo em JSON como este:' },
      { tipo: 'codigo', linguagem: 'json', texto: `{
  "evento": "sla_violado",
  "empresa_id": 1,
  "sat_num": 117,
  "usuario_id": 5,
  "chamado": {
    "tipo_id": 3,
    "prioridade": 2,
    "status": 20,
    "responsavel": 5,
    "categoria_id": 1
  },
  "timestamp": "2026-06-10T14:32:00.000Z"
}` },
      { tipo: 'paragrafo', texto: 'Segurança (assinatura): cada webhook tem um segredo gerado na criação. Antes de enviar, o Tacoma calcula um HMAC-SHA256 do corpo com esse segredo e envia no cabeçalho X-Tacoma-Signature (e o nome do evento em X-Tacoma-Evento). Do seu lado, recalcule a assinatura e compare para confirmar que a mensagem é legítima e não foi adulterada.' },
      { tipo: 'paragrafo', texto: 'Exemplo de quem recebe e valida (Node/Express):' },
      { tipo: 'codigo', linguagem: 'javascript', texto: `const crypto = require('crypto');
const SEGREDO = 'cole-aqui-o-segredo-do-webhook';

app.post('/recebe', express.raw({ type: '*/*' }), (req, res) => {
  const corpo = req.body.toString('utf8');
  const esperado = 'sha256=' + crypto
    .createHmac('sha256', SEGREDO)
    .update(corpo)
    .digest('hex');
  const recebida = req.header('X-Tacoma-Signature');
  if (recebida !== esperado) return res.status(401).end();

  const dados = JSON.parse(corpo);
  // ... faça algo com dados.evento e dados.chamado
  res.status(200).end();
});` },
      { tipo: 'nota', texto: 'O destino deve responder com status 2xx para a entrega ser considerada bem-sucedida. Toda tentativa fica registrada no log de "Entregas" de cada webhook. Use o botão "Testar" para enviar um payload de exemplo antes de depender da integração.' },
    ],
  },
  {
    id: 'api-publica',
    titulo: 'API pública (chaves + REST)',
    icone: 'fa-key',
    blocos: [
      { tipo: 'paragrafo', texto: 'Enquanto o webhook é o Tacoma avisando terceiros (saída), a API pública é o caminho inverso: permite que sistemas externos consultem e criem informações no Tacoma (entrada), usando uma chave de API por empresa.' },
      { tipo: 'paragrafo', texto: 'Onde gerar a chave: Config → Integrações → Chaves de API. Ao criar, escolha os escopos (o que a chave pode fazer). A chave completa é exibida uma única vez — copie e guarde com segurança; o Tacoma armazena apenas o hash.' },
      { tipo: 'paragrafo', texto: 'Autenticação: envie a chave em todas as chamadas, no cabeçalho Authorization (ou X-API-Key):' },
      { tipo: 'codigo', linguagem: 'bash', texto: `curl https://SEU-SERVIDOR/api/v1/chamados \\
  -H "Authorization: Bearer tac_suachavecompleta"` },
      { tipo: 'paragrafo', texto: 'Escopos disponíveis: chamados:ler, chamados:criar, inventario:ler, sprints:ler. Uma chamada sem o escopo necessário recebe HTTP 403; chave ausente/inválida recebe HTTP 401.' },
      { tipo: 'paragrafo', texto: 'Endpoints (base /api/v1):' },
      { tipo: 'passos', itens: [
        'GET /chamados — lista chamados (filtros: status, tipo, limit). Escopo: chamados:ler.',
        'GET /chamados/{numero} — detalhe do chamado + últimos trâmites. Escopo: chamados:ler.',
        'POST /chamados — cria um chamado. Escopo: chamados:criar.',
        'GET /inventario/hardware — lista de ativos (limit). Escopo: inventario:ler.',
        'GET /sprints — lista de sprints (limit). Escopo: sprints:ler.',
      ] },
      { tipo: 'paragrafo', texto: 'Exemplo — criar um chamado:' },
      { tipo: 'codigo', linguagem: 'bash', texto: `curl -X POST https://SEU-SERVIDOR/api/v1/chamados \\
  -H "Authorization: Bearer tac_suachavecompleta" \\
  -H "Content-Type: application/json" \\
  -d '{ "descricao": "Impressora sem conexão", "tipo_id": 3, "solicitante_id": 42 }'` },
      { tipo: 'paragrafo', texto: 'Resposta de listagem (exemplo):' },
      { tipo: 'codigo', linguagem: 'json', texto: `{
  "dados": [
    { "numero": 117, "descricao": "Impressora...", "status": 0,
      "tipo_id": 3, "prioridade": 2, "responsavel_id": 5,
      "aberto_em": "2026-06-10T12:00:00.000Z" }
  ],
  "total": 1
}` },
      { tipo: 'nota', texto: 'Boas práticas: trate a chave como senha (não a versione no código); gere uma chave por integração para poder revogar individualmente; conceda apenas os escopos necessários; e revogue chaves que não estão mais em uso na própria tela de Chaves de API.' },
    ],
  },
  {
    id: 'sla',
    titulo: 'Políticas de SLA',
    icone: 'fa-stopwatch',
    blocos: [
      { tipo: 'paragrafo', texto: 'O SLA define o prazo de resolução de um chamado. Onde configurar: Config → SLA → Políticas de SLA.' },
      { tipo: 'paragrafo', texto: 'Uma política tem um prazo (em horas) e um escopo: pode valer para um tipo de chamado, uma categoria ou de forma geral. A política mais específica vence (tipo > categoria > geral).' },
      { tipo: 'paragrafo', texto: 'O relógio do SLA conta apenas em horário de funcionamento (ver tópico Horário de funcionamento) e pode ser pausado em status definidos (ex.: "Aguardando cliente"). Há um limiar de risco (%) que, ao ser atingido, dispara o evento sla_risco; ao estourar, dispara sla_violado — ambos podem acionar automações e webhooks.' },
      { tipo: 'paragrafo', texto: 'O indicador de SLA aparece nos cards do chamado, mostrando o estado (no prazo, em risco, atrasado) e o tempo restante.' },
    ],
  },
  {
    id: 'automacao',
    titulo: 'Regras de automação',
    icone: 'fa-robot',
    blocos: [
      { tipo: 'paragrafo', texto: 'As automações executam ações automaticamente quando um evento ocorre. Onde configurar: Config → Automação → Regras de automação.' },
      { tipo: 'paragrafo', texto: 'Cada regra é: gatilho (evento) → condições → ações. O gatilho é um dos eventos do sistema. As condições (todas precisam ser verdadeiras) comparam campos do chamado (tipo, categoria, prioridade, status, responsável, sistema, cliente) com operadores (igual, diferente, contém, maior, menor).' },
      { tipo: 'paragrafo', texto: 'Ações disponíveis: atribuir responsável, mudar status, definir prioridade, notificar e comentar. Cada execução fica registrada no log da regra.' },
      { tipo: 'nota', texto: 'Os mesmos eventos que disparam automações também disparam os webhooks inscritos — as duas coisas convivem.' },
    ],
  },
  {
    id: 'horario',
    titulo: 'Horário de funcionamento e almoço',
    icone: 'fa-business-time',
    blocos: [
      { tipo: 'paragrafo', texto: 'Define a janela de trabalho usada no cálculo do SLA (horas úteis). Onde configurar: Config → Configuração da empresa → Horário de funcionamento.' },
      { tipo: 'paragrafo', texto: 'Você informa horário de início e fim, os dias úteis e, opcionalmente, o intervalo de almoço (saída e volta). Com o almoço preenchido, o dia útil passa a ter duas janelas (manhã e tarde) e o intervalo não conta no SLA.' },
      { tipo: 'paragrafo', texto: 'Feriados e expedientes especiais por data também podem ser cadastrados (fechado ou horário reduzido), afetando o cálculo apenas naquele dia.' },
    ],
  },
  {
    id: 'filtros-paineis',
    titulo: 'Filtros salvos e Painéis',
    icone: 'fa-table-columns',
    blocos: [
      { tipo: 'paragrafo', texto: 'Filtros salvos: na tela de Chamados em aberto, ajuste os filtros (tipos, atendentes, status, datas) e clique em "Salvar atual" para reutilizá-los depois. Você pode compartilhar um filtro com a equipe.' },
      { tipo: 'paragrafo', texto: 'Painéis: em Relatórios → Painéis você monta dashboards com widgets (cumprimento de SLA, fila por analista, lead/cycle time, fluxo de status, velocity de sprints e contadores). Entre em "Editar" para adicionar widgets; um contador pode usar um filtro salvo como fonte.' },
    ],
  },
  {
    id: 'busca-colaboracao',
    titulo: 'Busca global, watchers e menções',
    icone: 'fa-magnifying-glass',
    blocos: [
      { tipo: 'paragrafo', texto: 'Busca global: pressione Ctrl/Cmd+K em qualquer tela para abrir a paleta de busca e localizar chamados, artigos da base, sistemas, hardware, sprints e repositórios por texto natural.' },
      { tipo: 'paragrafo', texto: 'Seguidores (watchers): no chamado, clique em "Seguir" para receber notificação a cada atualização. Menções: em um comentário, digite @ e escolha o colega na lista — ele recebe uma notificação. As menções aparecem destacadas em azul no histórico.' },
    ],
  },
  {
    id: 'avaliacao',
    titulo: 'Avaliação de atendimento (CSAT)',
    icone: 'fa-star',
    blocos: [
      { tipo: 'paragrafo', texto: 'Ao fechar um chamado, o solicitante pode avaliar o atendimento de 1 a 5 estrelas e deixar um comentário, na tela do chamado fechado do portal do solicitante.' },
      { tipo: 'paragrafo', texto: 'A nota fica visível para o analista no próprio chamado e serve de base para indicadores de satisfação.' },
    ],
  },
  {
    id: 'cmdb',
    titulo: 'CMDB do inventário e QR code',
    icone: 'fa-sitemap',
    blocos: [
      { tipo: 'paragrafo', texto: 'No detalhe de um ativo (inventário), o bloco CMDB reúne: garantia (início, fim, fornecedor) com selo de status, licenças de software (nome, tipo, chave, validade) e sistemas relacionados ao equipamento.' },
      { tipo: 'paragrafo', texto: 'QR code: cada ativo tem um QR que aponta para a página do próprio ativo. Use "Imprimir etiqueta" para gerar uma etiqueta (QR + patrimônio) e colar no equipamento. Ao escanear com a câmera do celular, abre direto a página do ativo (pedindo login, se necessário).' },
      { tipo: 'nota', texto: 'O QR usa o mesmo endereço que está aberto no navegador ao gerar a etiqueta — gere a partir de um endereço acessível pelo celular (não use localhost).' },
    ],
  },
];

export default GUIA;
